/**
 * Kinship path finder (v0.18.0, D-110) — "How am I related to X?"
 *
 * A pure function over (persons, relationships), same shape as graph.ts, so the
 * SPA, a future API route, and Sage all share ONE implementation (D-078: any
 * behavior with 2+ entry points is a shared helper, never a copy).
 *
 * Arabic terms are first-class, not a translation afterthought: Arabic kinship
 * distinguishes the PATERNAL from the MATERNAL line (عم vs خال), so the algorithm
 * must track which side the path climbs — English can't tell you that.
 */

export type Rel = { type: string; person1_id: string; person2_id: string }
export type Pers = { id: string; first_name?: string; last_name?: string; gender?: string | null }

export type Relation = {
  term: string            // English, e.g. "second cousin once removed"
  term_ar: string | null  // Arabic (null when gender/side is unknown — never guess)
  path: string[]          // person ids, from → to
  degree?: number         // cousin degree (1 = first cousin)
  removed?: number        // generations removed
}

const parentsOf = (id: string, rels: Rel[]) =>
  rels.filter(r => r.type === 'parent' && r.person2_id === id).map(r => r.person1_id)
const childrenOf = (id: string, rels: Rel[]) =>
  rels.filter(r => r.type === 'parent' && r.person1_id === id).map(r => r.person2_id)
const spousesOf = (id: string, rels: Rel[]) =>
  rels.filter(r => r.type === 'spouse' && (r.person1_id === id || r.person2_id === id))
    .map(r => (r.person1_id === id ? r.person2_id : r.person1_id))

/** Ancestors of `id` → depth (0 = self). Cycle-safe. */
function ancestorDepths(id: string, rels: Rel[], maxDepth = 12): Map<string, number> {
  const out = new Map<string, number>([[id, 0]])
  let frontier = [id]
  for (let d = 1; d <= maxDepth && frontier.length; d++) {
    const next: string[] = []
    for (const p of frontier) {
      for (const par of parentsOf(p, rels)) {
        if (!out.has(par)) { out.set(par, d); next.push(par) }
      }
    }
    frontier = next
  }
  return out
}

const g = (p?: Pers) => (p?.gender === 'male' ? 'm' : p?.gender === 'female' ? 'f' : null)

const ORDINAL = ['', 'first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth']
const ordinal = (n: number) => ORDINAL[n] || `${n}th`
const removedWord = (n: number) => (n === 0 ? '' : n === 1 ? ' once removed' : n === 2 ? ' twice removed' : ` ${n} times removed`)

/** Great-great-…-grand prefix for direct lines. */
function greatPrefix(gens: number): string {
  if (gens <= 1) return ''
  if (gens === 2) return 'grand'
  return `${'great-'.repeat(gens - 2)}grand`
}

/**
 * Which side does the climb from `me` to the common ancestor go through —
 * father's line or mother's? Arabic needs this (عم = father's brother,
 * خال = mother's brother). Returns null when it can't be determined.
 */
function sideOfClimb(meId: string, ancestorId: string, rels: Rel[], persons: Map<string, Pers>): 'p' | 'm' | null {
  // walk up from me toward the ancestor; the FIRST step's parent decides the side
  const seen = new Set<string>([meId])
  let frontier: { id: string; firstParent?: string }[] = [{ id: meId }]
  for (let d = 0; d < 12 && frontier.length; d++) {
    const next: { id: string; firstParent?: string }[] = []
    for (const node of frontier) {
      for (const par of parentsOf(node.id, rels)) {
        const firstParent = node.firstParent ?? par
        if (par === ancestorId) {
          const fp = persons.get(firstParent)
          return g(fp) === 'm' ? 'p' : g(fp) === 'f' ? 'm' : null
        }
        if (!seen.has(par)) { seen.add(par); next.push({ id: par, firstParent }) }
      }
    }
    frontier = next
  }
  return null
}

function arabicFor(kind: string, gender: 'm' | 'f' | null, side: 'p' | 'm' | null, gens = 1): string | null {
  if (!gender) return null
  switch (kind) {
    case 'parent': return gender === 'm' ? 'أب' : 'أم'
    case 'grandparent': {
      const base = gender === 'm' ? 'جد' : 'جدة'
      return gens > 2 ? `${base} ${'الأكبر'}` : base
    }
    case 'child': return gender === 'm' ? 'ابن' : 'بنت'
    case 'grandchild': return gender === 'm' ? 'حفيد' : 'حفيدة'
    case 'sibling': return gender === 'm' ? 'أخ' : 'أخت'
    case 'parentSibling': // uncle/aunt — the distinction English lacks
      if (!side) return null
      if (side === 'p') return gender === 'm' ? 'عم' : 'عمة'
      return gender === 'm' ? 'خال' : 'خالة'
    case 'siblingChild': return gender === 'm' ? 'ابن الأخ' : 'بنت الأخ'
    case 'cousin':
      if (!side) return null
      if (side === 'p') return gender === 'm' ? 'ابن العم' : 'بنت العم'
      return gender === 'm' ? 'ابن الخال' : 'بنت الخال'
    case 'spouse': return gender === 'm' ? 'زوج' : 'زوجة'
    default: return null
  }
}

/**
 * Find how `toId` is related to `fromId` ("to is my ___").
 * Returns null when unrelated within the search depth.
 */
export function findRelation(
  fromId: string, toId: string, persons: Pers[], relationships: Rel[],
): Relation | null {
  if (!fromId || !toId) return null
  const byId = new Map(persons.map(p => [p.id, p]))
  const rels = relationships.filter(r => r.type === 'parent' || r.type === 'spouse')
  const target = byId.get(toId)
  const tg = g(target)

  if (fromId === toId) return { term: 'yourself', term_ar: 'أنت', path: [fromId] }

  // spouse (direct)
  if (spousesOf(fromId, rels).includes(toId)) {
    return { term: tg === 'm' ? 'husband' : tg === 'f' ? 'wife' : 'spouse', term_ar: arabicFor('spouse', tg, null), path: [fromId, toId] }
  }

  const mine = ancestorDepths(fromId, rels)
  const theirs = ancestorDepths(toId, rels)

  // lowest common ancestor = the one minimising combined distance
  let lca: string | null = null, best = Infinity, dm = 0, dt = 0
  for (const [aid, a] of mine) {
    const b = theirs.get(aid)
    if (b === undefined) continue
    if (a + b < best) { best = a + b; lca = aid; dm = a; dt = b }
  }

  if (!lca) {
    // in-law: my spouse's blood relation
    for (const sp of spousesOf(fromId, rels)) {
      const via = findRelation(sp, toId, persons, relationships)
      if (via) return { ...via, term: `${via.term}-in-law`, term_ar: via.term_ar ? `${via.term_ar} بالزواج` : null, path: [fromId, ...via.path] }
    }
    return null
  }

  const path = [fromId, lca, toId].filter((v, i, a) => a.indexOf(v) === i)

  // direct line: the LCA is one of us
  if (dm === 0) { // to is my descendant
    const term = dt === 1 ? (tg === 'm' ? 'son' : tg === 'f' ? 'daughter' : 'child')
      : `${greatPrefix(dt)}${tg === 'm' ? 'son' : tg === 'f' ? 'daughter' : 'child'}`
    return { term, term_ar: arabicFor(dt === 1 ? 'child' : 'grandchild', tg, null), path }
  }
  if (dt === 0) { // to is my ancestor
    const term = dm === 1 ? (tg === 'm' ? 'father' : tg === 'f' ? 'mother' : 'parent')
      : `${greatPrefix(dm)}${tg === 'm' ? 'father' : tg === 'f' ? 'mother' : 'parent'}`
    return { term, term_ar: arabicFor(dm === 1 ? 'parent' : 'grandparent', tg, null, dm), path }
  }

  const side = sideOfClimb(fromId, lca, rels, byId)

  // siblings
  if (dm === 1 && dt === 1) {
    const myParents = new Set(parentsOf(fromId, rels))
    const shared = parentsOf(toId, rels).filter(p => myParents.has(p)).length
    const half = shared < 2 && myParents.size > 1 ? 'half-' : ''
    return {
      term: `${half}${tg === 'm' ? 'brother' : tg === 'f' ? 'sister' : 'sibling'}`,
      term_ar: arabicFor('sibling', tg, null), path,
    }
  }
  // aunt/uncle  ·  niece/nephew
  if (dm >= 2 && dt === 1) {
    const greats = dm - 2
    const base = tg === 'm' ? 'uncle' : tg === 'f' ? 'aunt' : 'sibling of parent'
    return { term: `${greats ? `${'great-'.repeat(greats)}` : ''}${base}`, term_ar: arabicFor('parentSibling', tg, side), path }
  }
  if (dm === 1 && dt >= 2) {
    const greats = dt - 2
    const base = tg === 'm' ? 'nephew' : tg === 'f' ? 'niece' : 'sibling’s child'
    return { term: `${greats ? `${'great-'.repeat(greats)}` : ''}${base}`, term_ar: arabicFor('siblingChild', tg, null), path }
  }

  // cousins: degree = (closer climb) - 1 · removal = |difference|
  const degree = Math.min(dm, dt) - 1
  const removed = Math.abs(dm - dt)
  return {
    term: `${ordinal(degree)} cousin${removedWord(removed)}`,
    term_ar: degree === 1 && removed === 0 ? arabicFor('cousin', tg, side) : null,
    path, degree, removed,
  }
}
